import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, ChevronRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EventTypeResponse } from '@/client/types.gen';
import { getEventTypes } from '@/client/sdk.gen';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

export function EventTypesOverview() {
  const { toast } = useToast();
  const [eventTypes, setEventTypes] = useState<EventTypeResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEventTypes();
  }, []);

  const fetchEventTypes = async () => {
    setIsLoading(true);
    try {
      const response = await getEventTypes({
        query: {
          include_inactive: true,
          limit: 3, // Only get a few for the overview
        },
      });

      if (response.data) {
        setEventTypes(response.data.event_types);
      }
    } catch (error) {
      console.error('Failed to fetch event types:', error);
      toast({
        title: 'Error',
        description: 'Failed to load appointment types',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes
      ? `${hours} hr ${remainingMinutes} min`
      : `${hours} hr`;
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'ONE_ON_ONE':
        return 'One-on-One';
      case 'GROUP':
        return 'Group';
      case 'COLLECTIVE':
        return 'Collective';
      case 'ROUND_ROBIN':
        return 'Round Robin';
      default:
        return category;
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-md flex items-center">
          <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
          Appointment Types
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-2">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : eventTypes.length > 0 ? (
          <div className="space-y-2">
            {eventTypes.slice(0, 3).map((eventType) => (
              <div
                key={eventType.id}
                className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
              >
                <div>
                  <div className="font-medium text-sm">{eventType.name}</div>
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge variant="secondary" className="text-xs">
                      {getCategoryLabel(eventType.category ?? 'ONE_ON_ONE')}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDuration(eventType.duration)}
                    </span>
                  </div>
                </div>
                {!eventType.is_active && (
                  <Badge variant="outline" className="text-xs">
                    Inactive
                  </Badge>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-3 text-sm text-muted-foreground">
            <p>No appointment types created yet</p>
            <Button variant="outline" size="sm" className="mt-2" asChild>
              <Link to="/dashboard/event-types">
                <Plus className="mr-1 h-3 w-3" />
                Create your first appointment
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
      <CardFooter className="pt-2">
        <Button variant="ghost" className="w-full justify-between" asChild>
          <Link to="/dashboard/event-types">
            <span>Manage Appointment Types</span>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
